import React, { useState } from 'react';
import { Box, Grid, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { EventCategories } from './EventCategories';
import { EventDisplay } from './EventDisplay';
import { CategoryManagement } from '../CategoryManagement';

export const SchoolEvents: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('academic');
  const [isAddEventOpen, setIsAddEventOpen] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [scrollPosition, setScrollPosition] = useState(0);
  const [newEventCategory, setNewEventCategory] = useState<string>('');
  const [eventName, setEventName] = useState('');
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [isAddSubEventOpen, setIsAddSubEventOpen] = useState(false);
  const eventContainerRef = React.useRef<HTMLDivElement>(null);

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setExpandedEvents(new Set());
    // Reset scroll position of event container
    if (eventContainerRef.current) {
      eventContainerRef.current.scrollTop = 0;
    }
    // Reset all states and scroll position
    requestAnimationFrame(() => {
      if (eventContainerRef.current) {
        eventContainerRef.current.scrollTop = 0;
      }
    });
    setSelectedEventId('');
    setExpandedEvents(new Set());
    setScrollPosition(0);
  };

  const handleAddEvent = (categoryId: string) => {
    setNewEventCategory(categoryId);
    setIsAddEventOpen(true);
  };

  const handleAddSubEvent = (eventId: string) => {
    setSelectedEventId(eventId);
    setIsAddSubEventOpen(true);
  };

  const handleSaveEvent = () => {
    // Here you would typically save the new event
    console.log('New event:', { name: eventName, categoryId: newEventCategory });
    setEventName('');
    setIsAddEventOpen(false);
  };

  const handleSaveSubEvent = () => {
    // Here you would typically save the new subevent
    console.log('New subevent:', { name: eventName, parentEventId: selectedEventId });
    setEventName('');
    setIsAddSubEventOpen(false);
  };
  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      <Typography variant="h4">
        School Event Catalog
      </Typography>
      <Box>
        <CategoryManagement />
      </Box>
      
      <Grid container spacing={3} sx={{
        flex: 1, 
        minHeight: 0,
        position: 'relative'
      }}>
        <Grid item xs={12} md={3} sx={{ 
          position: 'sticky',
          top: 0,
          height: 'calc(100vh - 200px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <Box sx={{
            height: '100%',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: 8
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'background.default',
              borderRadius: 4
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'primary.light',
              borderRadius: 4,
              '&:hover': {
                bgcolor: 'primary.main'
              }
            }
          }}>
            <EventCategories
              selectedCategory={selectedCategory}
              onCategorySelect={handleCategorySelect}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={9} sx={{ 
          height: 'calc(100vh - 200px)',
          overflow: 'auto', 
          '&::-webkit-scrollbar': {
            width: 8
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'background.default',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'primary.light',
            borderRadius: 4,
            '&:hover': {
              bgcolor: 'primary.main'
            }
          }
        }}>
          <EventDisplay 
            ref={eventContainerRef}
            categoryId={selectedCategory}
            onAddEvent={handleAddEvent}
            onAddSubEvent={handleAddSubEvent}
          />
        </Grid>
      </Grid>
      
      <Dialog 
        open={isAddEventOpen} 
        onClose={() => setIsAddEventOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Add New Event</Typography>
            <IconButton onClick={() => setIsAddEventOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Event Name"
            fullWidth
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddEventOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEvent} variant="contained" disabled={!eventName.trim()}>
            Add Event
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog 
        open={isAddSubEventOpen} 
        onClose={() => setIsAddSubEventOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Add New Sub Event</Typography>
            <IconButton onClick={() => setIsAddSubEventOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Sub Event"
            fullWidth
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddSubEventOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSubEvent} variant="contained" disabled={!eventName.trim()}>
            Add Sub Event
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};